import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, CalendarDays } from 'lucide-react'
import { apiJson } from '../api'
import { hasAnyRole } from '../auth'

interface SessionDetail {
  id: string
  courseId: string
  courseName: string
  title: string
  format: 'classroom' | 'webinar'
  startAt: string
  endAt: string
  location?: string | null
  meetingUrl?: string | null
  capacity: number
  instructorName?: string | null
  status: 'scheduled' | 'cancelled' | 'completed'
}

const emptyForm = {
  title: '',
  format: 'classroom',
  date: '',
  startTime: '',
  endTime: '',
  location: '',
  meetingUrl: '',
  capacity: '',
  instructorName: '',
  status: 'scheduled',
}

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

function toLocalParts(iso: string) {
  const d = new Date(iso)
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  return { date, time }
}

export default function SessionForm() {
  const { id: courseId, sessionId } = useParams()
  const isEdit = Boolean(sessionId)
  const navigate = useNavigate()
  const allowManage = hasAnyRole('admin', 'hr')

  const [courseName, setCourseName] = useState('')
  const [form, setForm] = useState(emptyForm)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      if (isEdit) {
        const s = await apiJson<SessionDetail>(`/sessions/${sessionId}`)
        const start = toLocalParts(s.startAt)
        const end = toLocalParts(s.endAt)
        setForm({
          title: s.title,
          format: s.format,
          date: start.date,
          startTime: start.time,
          endTime: end.time,
          location: s.location ?? '',
          meetingUrl: s.meetingUrl ?? '',
          capacity: s.capacity ? String(s.capacity) : '',
          instructorName: s.instructorName ?? '',
          status: s.status,
        })
        setCourseName(s.courseName)
      } else {
        const c = await apiJson<{ title: string; format: string; instructorName?: string | null }>(`/courses/${courseId}`)
        setCourseName(c.title)
        setForm((f) => ({
          ...f,
          format: c.format === 'webinar' ? 'webinar' : 'classroom',
          instructorName: c.instructorName ?? '',
        }))
      }
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [courseId, sessionId, isEdit])

  useEffect(() => {
    load()
  }, [load])

  const valid =
    form.title.trim().length > 0 &&
    form.date &&
    form.startTime &&
    form.endTime &&
    (form.format === 'webinar' ? form.meetingUrl.trim().length > 0 : form.location.trim().length > 0)

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const payload: Record<string, unknown> = {
        title: form.title.trim(),
        format: form.format,
        startAt: new Date(`${form.date}T${form.startTime}`).toISOString(),
        endAt: new Date(`${form.date}T${form.endTime}`).toISOString(),
        capacity: form.capacity ? parseInt(form.capacity, 10) : 0,
      }
      if (form.location.trim()) payload.location = form.location.trim()
      if (form.meetingUrl.trim()) payload.meetingUrl = form.meetingUrl.trim()
      if (form.instructorName.trim()) payload.instructorName = form.instructorName.trim()
      if (isEdit) payload.status = form.status
      if (isEdit) {
        await apiJson(`/sessions/${sessionId}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
          headers: { 'Content-Type': 'application/json' },
        })
      } else {
        await apiJson(`/courses/${courseId}/sessions`, {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: { 'Content-Type': 'application/json' },
        })
      }
      navigate('/learning')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
      setSaving(false)
    }
  }

  if (!allowManage) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        <AlertCircle size={16} className="shrink-0" />
        Only HR or admin users can manage sessions.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  const backTo = isEdit ? '/learning' : `/courses/${courseId}`

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> {isEdit ? 'Back to Learning' : 'Back to course'}
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <CalendarDays size={20} />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight">{isEdit ? 'Edit Session' : 'Schedule Session'}</h1>
            <p className="text-xs text-slate-400">{courseName}</p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          className="space-y-4 px-6 py-6"
        >
          <div>
            <label className={labelCls}>Session title *</label>
            <input required maxLength={200} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Cohort 1 — Main Hall" className={inputCls} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Date *</label>
              <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Start *</label>
              <input required type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>End *</label>
              <input required type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Format</label>
              <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className={inputCls}>
                <option value="classroom">Classroom</option>
                <option value="webinar">Webinar</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Capacity</label>
              <input type="number" min={0} max={100000} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="0 = unlimited" className={inputCls} />
            </div>
          </div>

          {form.format === 'classroom' ? (
            <div>
              <label className={labelCls}>Location *</label>
              <input maxLength={300} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Main Hall, Building A" className={inputCls} />
            </div>
          ) : (
            <div>
              <label className={labelCls}>Meeting URL *</label>
              <input maxLength={2000} value={form.meetingUrl} onChange={(e) => setForm({ ...form, meetingUrl: e.target.value })} placeholder="https://teams.microsoft.com/…" className={inputCls} />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Instructor</label>
              <input maxLength={200} value={form.instructorName} onChange={(e) => setForm({ ...form, instructorName: e.target.value })} placeholder="Optional" className={inputCls} />
            </div>
            {isEdit && (
              <div>
                <label className={labelCls}>Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                  <option value="scheduled">Scheduled</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Link
              to={backTo}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving || !valid}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEdit ? 'Save session' : 'Schedule session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
